<template>
  <Modal>
    <template #activator="{ onClick }">
      <div class="participant-list__button-wrapper">
        <button
          class="participant-list__button"
          @click="
            () => {
              onClick();
              handleOpen();
            }
          "
        >
          {{ $t('our_donors.see_all') }}
        </button>
      </div>
    </template>
    <template #content>
      <div class="participants-modal">
        <Loader v-if="isLoading" class="participants-modal__inner" />
        <div v-else ref="listRoot" class="participants-modal__inner">
          <div class="participants-modal__head">
            <p
              class="participants-modal__subtitle"
              v-html="$t('our_donors.modal_title')"
            />
            <h2 class="card__title" v-html="title" />
          </div>
          <PagesProjectParticipant
            v-for="participant in participants"
            :key="participant.id"
            :participant="participant"
            :locale="locale"
          />
          <div
            v-if="hasMore"
            ref="loadMoreTrigger"
            class="participants-modal__sentinel"
          />
          <Loader
            v-if="isLoadingMore"
            class="participants-modal__more"
            icon-width="32"
            icon-height="32"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import camelcaseKeys from 'camelcase-keys';

const { $t } = useI18n({});
const { $useApi } = useNuxtApp();

const props = defineProps({
  projectId: {
    type: String,
    required: true,
  },
  locale: {
    type: String,
    default: 'en',
  },
  participantsCount: {
    type: Number,
    required: true,
  },
});

const PAGE_SIZE = 100;
const LOAD_MORE_MARGIN = 160;

const listRoot = ref(null);
const loadMoreTrigger = ref(null);

const isLoading = ref(false);
const isLoadingMore = ref(false);
const participants = ref([]);
const summary = ref({});
const totalCount = ref(0);

const hasMore = computed(
  () => participants.value.length < totalCount.value,
);

useIntersectionObserver(
  loadMoreTrigger,
  (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      loadMore();
    }
  },
  {
    root: listRoot,
    rootMargin: `${LOAD_MORE_MARGIN}px 0px`,
  },
);

const title = computed(() => {
  const translateKy = getWordForm(summary.value.countParticipants, [
    'our_donors.x1_donations_for',
    'our_donors.x2_donations_for',
    'our_donors.x5_donations_for',
  ]);

  return $t(translateKy, {
    count: summary.value.countParticipants,
    amount: `$${(summary.value.donationCurrent / 100).toLocaleString(
      props.locale
    )}`,
  });
});

function handleOpen() {
  if (participants.value.length || isLoading.value) return;
  loadParticipants({ reset: true });
}

function loadMore() {
  if (isLoading.value || isLoadingMore.value || !hasMore.value) return;
  loadParticipants();
}

function isTriggerVisible() {
  const trigger = loadMoreTrigger.value;
  const root = listRoot.value;
  if (!trigger || !root) return false;

  const rootRect = root.getBoundingClientRect();
  const triggerRect = trigger.getBoundingClientRect();

  return triggerRect.top <= rootRect.bottom + LOAD_MORE_MARGIN;
}

async function loadParticipants({ reset = false } = {}) {
  if (isLoading.value || isLoadingMore.value) return;

  if (reset) {
    isLoading.value = true;
  } else {
    isLoadingMore.value = true;
  }

  const offset = reset ? 0 : participants.value.length;
  const headers = useCustomRequestHeaders();
  let succeeded = false;

  try {
    const response = await $fetch($useApi.project.getParticipants, {
      headers,
      query: {
        limit: PAGE_SIZE,
        offset,
        project_id: props.projectId,
      },
    });

    const transformResponse = camelcaseKeys(response, { deep: true });
    const nextParticipants = transformResponse.participants ?? [];

    participants.value = reset
      ? nextParticipants
      : [...participants.value, ...nextParticipants];
    summary.value = transformResponse.summary ?? {};
    totalCount.value =
      nextParticipants.length < PAGE_SIZE
        ? participants.value.length
        : (transformResponse.count ?? props.participantsCount);
    succeeded = true;
  } catch (error) {
    console.error('Error fetching participants:', error);
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }

  if (!succeeded || !hasMore.value) return;

  await nextTick();
  if (isTriggerVisible()) loadMore();
}

function getWordForm(number, forms) {
  const cases = [2, 0, 1, 1, 1, 2];
  const index =
    number % 100 > 4 && number % 100 < 20 ? 2 : cases[Math.min(number % 10, 5)];
  return forms[index];
}
</script>

<style lang="scss">
@use '@styles/components/pages/project/participantModal.scss' as *;
</style>
